import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { normalizeRole } from "@/hooks/useRoleAccess";
import type { AuthError, User, Session } from "@supabase/supabase-js";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type AuthResult = { error: AuthError | null };

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isPlatformAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string, fullName: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchingForRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string): Promise<Profile | null | undefined> => {
    if (fetchingForRef.current === userId) return undefined;
    fetchingForRef.current = userId;
    let { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    // If the user has no business_id, check for a pending invitation and auto-claim it.
    // This covers all sign-in methods (OAuth, email/password) in one place, replacing the
    // race-prone claiming in AuthCallbackPage.
    if (data && !data.business_id) {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser?.email) {
        const { data: invitation } = await supabase
          .from("team_invitations")
          .select("id, business_id, role")
          .eq("email", authUser.email.toLowerCase())
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (invitation) {
          await supabase.from("profiles").update({
            business_id: invitation.business_id,
            role: normalizeRole(invitation.role) ?? "agent",
          }).eq("id", userId);
          await supabase.from("team_invitations").update({
            status: "accepted",
            accepted_at: new Date().toISOString(),
          }).eq("id", invitation.id);
          // Re-fetch so the profile in memory reflects the claimed business_id.
          const { data: updated } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .maybeSingle();
          data = updated;
        }
      }
    }

    const nextProfile = data;
    setProfile(nextProfile);
    setIsPlatformAdmin(nextProfile?.is_platform_admin === true);
    // Cache business_id so subsequent loads can fire dashboard queries immediately
    // before this profile round-trip completes.
    if (nextProfile?.business_id) {
      try {
        localStorage.setItem("er_bid_hint", nextProfile.business_id);
      } catch {
        return nextProfile;
      }
    }
    return nextProfile;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          localStorage.removeItem("demo_mode");
          // Use setTimeout to avoid Supabase deadlock, but keep loading until profile is fetched
          setTimeout(async () => {
            try {
              await fetchProfile(session.user.id);
            } catch {
              // Profile fetch failed — user is still authenticated, app renders with null profile
            } finally {
              setLoading(false);
            }
          }, 0);
        } else {
          setProfile(null);
          setIsPlatformAdmin(false);
          fetchingForRef.current = null;
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        try {
          await fetchProfile(session.user.id);
        } catch {
          // Leave profile unset if the fetch fails during initial hydration.
        }
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setIsPlatformAdmin(false);
    fetchingForRef.current = null;
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, isPlatformAdmin, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
